import dayjs from 'dayjs';

import { isFutureAppointment } from './courses';
import { removeTrailingSlash } from './utilities';

export const getJsonLd = ({
	path,
	breadcrumbItems,
	organization,
	linkedInUrl,
	xingUrl,
	courses,
	courseTypeImageUrls,
	logo,
	blogPost,
}) => {
	const urlWithoutTrailingSlash = removeTrailingSlash(organization.url);
	const fullUrl = `${urlWithoutTrailingSlash}${path}`;

	const breadcrumbListSchema = getBreadcrumbListSchema({
		breadcrumbItems,
		urlWithoutTrailingSlash,
	});

	const organizationSchema = getOrganizationSchema({
		organization,
		logo,
		linkedInUrl,
		xingUrl,
	});

	const localBusinessSchema = getLocalBusinessSchema({
		organization,
		logo,
	});

	let schema = {
		'@context': 'http://schema.org/',
		'@graph': [
			{
				'@type': 'WebSite',
				url: fullUrl,
				provider: organizationSchema,
			},
			organizationSchema,
			breadcrumbListSchema,
			localBusinessSchema,
		],
	};

	if (courses) {
		const courseSchema = getCourseSchema({
			fullUrl,
			courses,
			courseTypeImageUrls,
			organizationSchema,
		});

		schema = {
			...schema,
			'@graph': [...schema['@graph'], ...courseSchema],
		};
	}

	if (blogPost) {
		const blogPostSchema = getBlogPostSchema({
			blogPost,
			organizationSchema,
		});

		schema = {
			...schema,
			...blogPostSchema,
		};
	}
	return schema;
};

const getBreadcrumbListSchema = ({ breadcrumbItems, urlWithoutTrailingSlash }) => {
	return {
		'@type': 'BreadcrumbList',
		itemListElement: breadcrumbItems.map((item, idx) => ({
			'@type': 'ListItem',
			position: idx + 1,
			name: item.title,
			item: `${urlWithoutTrailingSlash}${item.to}`,
		})),
	};
};

const getOrganizationSchema = ({ organization, logo, linkedInUrl, xingUrl }) => {
	const { name, legalName, url, address, contactPoint } = organization;
	return {
		'@type': 'Organization',
		name,
		legalName,
		url,
		logo,
		address: {
			'@type': 'PostalAddress',
			streetAddress: address.streetAddress,
			addressLocality: address.addressLocality,
			postalCode: address.postalCode,
			addressCountry: {
				'@type': 'Country',
				name: address.addressCountry,
			},
		},
		contactPoint: {
			'@type': 'ContactPoint',
			telephone: contactPoint.telephone,
			email: contactPoint.email,
		},
		sameAs: [linkedInUrl, xingUrl],
	};
};

const getCourseSchema = ({ fullUrl, courses, courseTypeImageUrls, organizationSchema }) => {
	return courses.map((course) => ({
		'@type': 'Course',
		provider: organizationSchema,
		name: course.title,
		description: course.seo.description,
		about: course.courseTypeTitle,
		url: fullUrl,
		inLanguage: {
			'@type': 'Language',
			name: 'German',
		},
		offers: {
			'@type': 'Offer',
			category: 'Paid',
			price: course.price,
			priceCurrency: 'EUR',
			url: fullUrl,
			availability: 'LimitedAvailability',
		},
		hasCourseInstance: course.appointments.filter(isFutureAppointment).map((appointment) =>
			getCourseInstanceSchema({
				fullUrl,
				course,
				image: courseTypeImageUrls[course.courseTypeUuid],
				organizationSchema,
				appointment,
			}),
		),
	}));
};

const getCourseInstanceSchema = ({ fullUrl, course, image, organizationSchema, appointment }) => {
	const days = dayjs(appointment.end).diff(dayjs(appointment.start), 'days') + 1;
	const startDateTime = dayjs(appointment.start);
	// end date contains the end time, must be normalized to the same date as start date
	const endDateTime = dayjs(appointment.start)
		.set('hour', dayjs(appointment.end).hour())
		.set('minute', dayjs(appointment.end).minute());
	const durationPerDay = endDateTime.diff(startDateTime, 'hours');
	return {
		'@type': 'CourseInstance',
		name: course.title,
		courseMode: appointment.location ? 'onsite' : 'online',
		eventStatus: 'EventScheduled',
		performer: organizationSchema,
		description: course.seo.description,
		startDate: appointment.start,
		endDate: appointment.end,
		courseSchedule: {
			'@type': 'Schedule',
			startDate: appointment.start,
			endDate: appointment.end,
			duration: `PT${durationPerDay}H`,
			repeatFrequency: 'Daily',
			repeatCount: days,
		},
		image,
		location: appointment.location
			? {
					'@type': 'Place',
					name: appointment.location,
					address: getPostalAddressSchema({
						streetAddress: appointment.location.split(',')[0],
						addressLocality: appointment.location.match(/\d{5}/)[0],
						postalCode: appointment.location.split(' ').slice(-1),
						addressCountry: organizationSchema.address.addressCountry,
					}),
				}
			: undefined,
		offers: {
			'@type': 'Offer',
			category: 'Paid',
			price: course.price,
			priceCurrency: 'EUR',
			url: fullUrl,
			availability: 'LimitedAvailability',
		},
		organizer: organizationSchema,
		eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
	};
};

const getPostalAddressSchema = ({
	streetAddress,
	addressLocality,
	postalCode,
	addressCountry,
}) => ({
	'@type': 'PostalAddress',
	streetAddress,
	addressLocality,
	postalCode,
	addressCountry,
});

const getLocalBusinessSchema = ({ organization, logo }) => {
	const {
		contactPoint: { telephone },
		name,
		url,
		address: { streetAddress, addressLocality, postalCode, addressCountry },
	} = organization;

	return {
		'@type': 'LocalBusiness',
		'@id': url,
		address: getPostalAddressSchema({
			streetAddress,
			addressLocality,
			postalCode,
			addressCountry,
		}),
		name,
		telephone,
		url,
		logo,
		image: logo,
		openingHoursSpecification: getOpeningHoursSpecificationSchema(),
		geo: {
			'@type': 'GeoCoordinates',
			latitude: '53.561329',
			longitude: '9.9573353',
		},
	};
};

const getOpeningHoursSpecificationSchema = () => [
	{
		'@type': 'OpeningHoursSpecification',
		dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
		opens: '09:00',
		closes: '19:00',
	},
];

const getBlogPostSchema = ({ blogPost, organizationSchema }) => {
	const authors = !!blogPost.authors
		? {
				author: blogPost.authors.map((author) => ({
					'@type': 'Person',
					name: author.name,
				})),
			}
		: {};

	return {
		'@type': 'BlogPosting',
		headline: blogPost.title,
		datePublished: blogPost.publishDate,
		image: blogPost.image,
		...authors,
		publisher: organizationSchema,
	};
};
